import type { FC } from "react";
import { useLayoutEffect, useRef, useState } from "react";

import { BasicModal, Button, Textfield } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";

import {
    CUSTOM_MODAL_STYLES,
    StyledButtonsContainer,
    StyledHeader,
    StyledModalContent,
    StyledNameInputContainer,
    StyledNameInputLabel,
    StyledTitle,
} from "./styles";

type UserRoleModalProps = {
    isOpened: boolean;
    onCancel: VoidFunction;
    onSubmit: (name: string) => void;
};

const UserRoleModal: FC<UserRoleModalProps> = ({ isOpened, onCancel, onSubmit }) => {
    const [name, setName] = useState<string>("");
    const textFieldRef = useRef<Textfield | null>(null);

    useLayoutEffect(() => {
        if (!isOpened) {
            setName("");
            return;
        }

        const timeout = setTimeout(() => {
            textFieldRef.current?.select();
        });

        return () => {
            setName("");
            clearTimeout(timeout);
        };
    }, [isOpened]);

    const handleSubmit = () => {
        onSubmit(name.trim());
    };

    return (
        <BasicModal
            isOpen={isOpened}
            closeIconConfig={{
                show: false,
            }}
            shouldCloseOnOverlayClick={false}
            customStyles={CUSTOM_MODAL_STYLES}
        >
            <StyledModalContent>
                <StyledHeader>
                    <I18n
                        data-automation="title-text"
                        element={StyledTitle}
                        translatable={{
                            key: "si.onboarding.add-role-modal.title",
                        }}
                    />
                </StyledHeader>
                <StyledNameInputContainer>
                    <I18n
                        data-automation="name-input-label-text"
                        element={StyledNameInputLabel}
                        translatable={{
                            key: "si.onboarding.add-role-modal.text-field.label",
                        }}
                    />
                    <Textfield
                        ref={textFieldRef}
                        autoFocus
                        maxLength={100}
                        onChange={setName}
                        dataAutomation="onboarding-role-modal-input"
                        placeholder={translate("si.onboarding.add-role-modal.placeholder")}
                    />
                </StyledNameInputContainer>
                <StyledButtonsContainer>
                    <Button
                        type="flat"
                        onClick={onCancel}
                        dataAutomation="onboarding-add-role-modal-button-cancel"
                    >
                        {translate("common.cancel")}
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        dataAutomation="onboarding-add-role-modal-button-submit"
                    >
                        {translate("common.submit")}
                    </Button>
                </StyledButtonsContainer>
            </StyledModalContent>
        </BasicModal>
    );
};

export default UserRoleModal;
